import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, CATEGORIES, GEMSTONES } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";

const FilterRow = ({ options, active, onSelect, testId, prefix }) => (
  <div className="flex flex-wrap gap-3 sm:gap-4" data-testid={testId}>
    {options.map((o) => (
      <button
        key={o.value}
        onClick={() => onSelect(o.value)}
        data-testid={`${prefix}-${o.value}`}
        className={`rounded-full border px-5 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 sm:px-6 sm:py-2.5 sm:text-xs ${
          active === o.value
            ? "border-[#A58343] bg-[#A58343] text-[#262019]"
            : "border-[#A58343]/40 text-[#A58343] hover:border-[#A58343]"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const gemstone = searchParams.get("gemstone") || "all";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const update = (next) => {
    const params = { category, gemstone, ...next };
    setSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "all")));
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (gemstone !== "all") params.gemstone = gemstone;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, gemstone]);

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 pt-40 lg:px-8 lg:pb-32" data-testid="collections-page">
      <Reveal>
        <p className="overline">The Catalogue</p>
        <h1 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl lg:text-6xl">
          Every piece, an <span className="italic text-[#A58343]">heirloom</span> in waiting
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-[#7C7163]">
          Polki sets, necklaces, bracelets, rings and earrings — real diamonds, made by hand.
          Photography from our Kolkata shoot is being added piece by piece; pricing and
          availability are shared personally during your private consultation.
        </p>
      </Reveal>

      <div className="mt-12 space-y-5">
        <FilterRow options={CATEGORIES} active={category} onSelect={(v) => update({ category: v })} testId="category-filters" prefix="filter" />
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#7C7163]">By stone</span>
          <FilterRow options={GEMSTONES} active={gemstone} onSelect={(v) => update({ gemstone: v })} testId="gemstone-filters" prefix="gem" />
        </div>
      </div>

      {loading ? (
        <p className="mt-24 text-center text-sm tracking-[0.2em] text-[#7C7163]" data-testid="products-loading">
          UNVEILING THE COLLECTION…
        </p>
      ) : products.length === 0 ? (
        <p className="mt-24 text-center text-sm tracking-[0.2em] text-[#7C7163]" data-testid="products-empty">
          NO PIECES IN THIS SELECTION YET — MORE ARRIVE WITH EACH SHOOT
        </p>
      ) : (
        <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
          {products.map((p, i) => (
            <Reveal key={p.slug} delay={0.05 * (i % 3)}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
